import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState, useEffect } from 'react';
import { Clock, Loader2, TrendingUp } from 'lucide-react';
import { logger } from '@/lib/logger';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface SubscriptionChange {
  id: string;
  plan_tier: string;
  status: string;
  changed_at: string;
  period_start: string;
  period_end: string;
  is_current: boolean;
}

export function SubscriptionHistory() {
  const { toast } = useToast();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<SubscriptionChange[]>([]);

  useEffect(() => {
    fetchHistory();
  }, [isImpersonating, impersonatedUser?.id]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      
      // Prepare headers for impersonation if active
      const headers: Record<string, string> = {};
      if (isImpersonating && impersonatedUser?.id) {
        headers['X-Impersonated-User-Id'] = impersonatedUser.id;
      }

      const { data, error } = await supabase.functions.invoke('get-subscription-history', {
        headers,
      });

      if (error) throw error;

      setHistory(data.history || []);
    } catch (error) {
      logger.error('Error fetching subscription history:', error);
      toast({
        title: 'Failed to Load History',
        description: error instanceof Error ? error.message : 'Could not fetch subscription history',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'trialing':
        return 'secondary';
      case 'past_due':
        return 'destructive';
      case 'canceled':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription History</CardTitle>
          <CardDescription>Loading your subscription history...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription History</CardTitle>
          <CardDescription>Track your plan changes and subscription events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No subscription history found</p>
            <p className="text-sm mt-2">Your plan changes will appear here</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription History</CardTitle>
        <CardDescription>
          Track your plan changes and subscription events
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Changed</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((change) => (
                <TableRow key={change.id}>
                  <TableCell className="font-medium capitalize">
                    {change.plan_tier}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusColor(change.status)} className="capitalize">
                      {change.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(change.period_start)} - {formatDate(change.period_end)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(change.changed_at)}
                  </TableCell>
                  <TableCell>
                    {change.is_current && (
                      <Badge variant="default" className="gap-1">
                        <TrendingUp className="h-3 w-3" />
                        Current
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
